import "@/App.css";
import "@/studio.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { ABTestProvider } from "@/contexts/ABTestContext";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import LiveChat from "@/components/LiveChat";
import { ScrollProgress } from "@/components/animations";
import HomePage from "@/pages/HomePage";
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const NewReviewPage = lazy(() => import("@/pages/NewReviewPage"));
const BeforeAfterGallery = lazy(() => import("@/pages/BeforeAfterGallery"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const EcoProductsPage = lazy(() => import("@/pages/EcoProductsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const PageLoading = () => <div role="status" data-testid="page-loading" className="min-h-[60vh] flex items-center justify-center gap-4 pt-24 text-navy-900"><span aria-hidden="true" className="w-6 h-6 border-2 border-slate-200 border-t-marine-600 rounded-full motion-safe:animate-spin" />Shylov Cleaning</div>;

// Page transition animation variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 8
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.24,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.16,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
};

// Animated Routes wrapper
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={pageVariants}
      >
        <Suspense fallback={<PageLoading />}>
        <Routes location={location}>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/before-after" element={<BeforeAfterGallery />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/reviews/new" element={<NewReviewPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/eco-products" element={<EcoProductsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  // Register/update Service Worker on app load
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => {
        // Check for updates immediately
        reg.update();
      }).catch(() => {});
    }
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <HelmetProvider>
      <LanguageProvider>
        <ABTestProvider>
          <div className="App min-h-screen flex flex-col">
            <BrowserRouter>
              <ScrollProgress position="top" />
              <ScrollToTop />
              <Header />
              <main className="flex-grow">
                <AnimatedRoutes />
              </main>
              <Footer />
              <WhatsAppButton />
              <LiveChat />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </div>
        </ABTestProvider>
      </LanguageProvider>
    </HelmetProvider>
    </MotionConfig>
  );
}

export default App;
